import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://ticobaterias.com/api';
const POR_PAGINA = 50;

type Filtro = 'todos' | 'ofertas' | 'sin_ofertas' | 'baja';

type Cliente = {
  id: number;
  nombre: string;
  telefono: string;
  correo: string;
  marca_bateria: string;
  acepta_ofertas: boolean;
  consentimiento_en: string | null;
  consentimiento_texto: string;
  consentimiento_origen: string;
  baja: boolean;
  baja_en: string | null;
  baja_motivo: string;
  origen: string;
  creado_en: string;
  ultima_interaccion: string | null;
  participaciones: number;
};

type Respuesta = {
  total: number;
  pagina: number;
  resumen: { total: number; ofertas: number; baja: number };
  clientes: Cliente[];
};

const FILTROS: { id: Filtro; etiqueta: string }[] = [
  { id: 'todos', etiqueta: 'Todos' },
  { id: 'ofertas', etiqueta: '✅ Aceptan ofertas' },
  { id: 'sin_ofertas', etiqueta: 'Sin autorización' },
  { id: 'baja', etiqueta: '🚫 Dados de baja' },
];

const fecha = (s: string | null | undefined) =>
  s ? new Date(/(Z|[+-]\d{2}:\d{2})$/.test(s) ? s : s + 'Z').toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' }) : '';

const whatsapp = (tel: string) => {
  const d = tel.replace(/\D/g, '');
  return d.length === 8 ? `https://wa.me/506${d}` : '';
};

function Dato({ titulo, valor, detalle }: { titulo: string; valor: number; detalle?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="text-gray-500 text-xs font-medium">{titulo}</div>
      <div className="text-2xl font-bold text-azul mt-1">{valor.toLocaleString('es-CR')}</div>
      {detalle && <div className="text-xs text-gray-400 mt-1">{detalle}</div>}
    </div>
  );
}

export default function Clientes() {
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [consulta, setConsulta] = useState('');
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState<number | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const mostrarAviso = (tipo: 'ok' | 'error', texto: string) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };

  const cargar = () =>
    axios.get(`${API}/clientes`, { headers, params: { filtro, q: consulta, pagina, limite: POR_PAGINA } })
      .then(r => setDatos(r.data))
      .catch(() => mostrarAviso('error', '❌ No se pudieron cargar los clientes'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [filtro, consulta, pagina]);

  // Busca medio segundo despues de dejar de escribir
  useEffect(() => {
    const t = setTimeout(() => { setPagina(1); setConsulta(busqueda.trim()); }, 500);
    return () => clearTimeout(t);
  }, [busqueda]);

  const accion = async (c: Cliente, tipo: 'baja' | 'reactivar' | 'eliminar') => {
    try {
      if (tipo === 'baja') {
        const motivo = window.prompt(`¿Dar de baja a ${c.nombre}? No recibirá más ofertas.\nMotivo:`, 'Escribió BAJA por WhatsApp');
        if (motivo === null) return;
        await axios.post(`${API}/clientes/${c.id}/baja`, { motivo }, { headers });
        mostrarAviso('ok', '✅ Cliente dado de baja');
      } else if (tipo === 'reactivar') {
        const motivo = window.prompt(
          `Solo reactiva si ${c.nombre} pidió volver a recibir ofertas.\n¿Cómo lo pidió?`, 'Lo pidió por WhatsApp');
        if (motivo === null) return;
        await axios.post(`${API}/clientes/${c.id}/reactivar`, { motivo }, { headers });
        mostrarAviso('ok', '✅ Cliente reactivado');
      } else {
        if (!window.confirm(`¿Borrar a ${c.nombre} de la base de clientes?\nSus participaciones en sorteos se conservan. No se puede deshacer.`)) return;
        await axios.delete(`${API}/clientes/${c.id}`, { headers });
        mostrarAviso('ok', '✅ Cliente eliminado');
      }
      cargar();
    } catch {
      mostrarAviso('error', '❌ No se pudo completar la acción');
    }
  };

  const exportar = async () => {
    try {
      const r = await axios.get(`${API}/clientes/exportar`, { headers, params: { filtro, q: consulta }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes-${filtro}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      mostrarAviso('error', '❌ No se pudo descargar');
    }
  };

  const paginas = datos ? Math.max(1, Math.ceil(datos.total / POR_PAGINA)) : 1;
  const r = datos?.resumen;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl text-azul">Clientes</h1>
          <p className="text-gray-500 text-sm">Base de clientes para ofertas y promociones</p>
        </div>
        {aviso && (
          <div className={`px-4 py-2 rounded-xl text-sm font-bold ${aviso.tipo === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {aviso.texto}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Dato titulo="Clientes en la base" valor={r?.total ?? 0} />
        <Dato titulo="Aceptan ofertas" valor={r?.ofertas ?? 0} detalle="A quienes sí se les puede escribir" />
        <Dato titulo="Dados de baja" valor={r?.baja ?? 0} detalle="Nunca enviarles ofertas" />
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-600 mb-6 space-y-1">
        <p>📣 <b>Envía ofertas solo a quienes aparecen en «Aceptan ofertas».</b> Para una campaña, entra a esa pestaña y descarga el Excel desde ahí: trae solo esa lista.</p>
        <p>🚫 Si alguien escribe <b>BAJA</b> o pide no recibir más mensajes, búscalo y usa <b>Dar de baja</b> el mismo día.</p>
        <p>📋 «Sin autorización» son clientes que participaron antes de que el formulario incluyera la autorización de ofertas.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
          <div className="inline-flex flex-wrap bg-gray-50 border border-gray-200 rounded-xl p-1">
            {FILTROS.map(f => (
              <button key={f.id} onClick={() => { setFiltro(f.id); setPagina(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${filtro === f.id ? 'bg-azul text-white' : 'text-gray-500 hover:text-azul'}`}>
                {f.etiqueta}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar nombre, teléfono, correo, marca..."
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-azul w-72 max-w-full" />
            <button onClick={exportar} disabled={!datos?.total}
              className="border border-azul text-azul rounded-lg px-3 py-2 text-sm font-bold hover:bg-blue-50 disabled:opacity-40">
              ⬇ Excel (CSV)
            </button>
          </div>
        </div>

        {!datos ? (
          <div className="text-center text-gray-400 py-10">Cargando...</div>
        ) : datos.clientes.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-10">
            {consulta ? 'Sin coincidencias.' : 'Todavía no hay clientes en esta lista. Se llena sola con cada registro de la rifa.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Cliente</th>
                  <th className="py-2 pr-3 font-medium">Contacto</th>
                  <th className="py-2 pr-3 font-medium">Batería</th>
                  <th className="py-2 pr-3 font-medium">Ofertas</th>
                  <th className="py-2 pr-3 font-medium">Última vez</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {datos.clientes.map(c => (
                  <Fragment key={c.id}>
                    <tr className={c.baja ? 'opacity-60' : ''}>
                      <td className="py-2.5 pr-3">
                        <button onClick={() => setAbierto(abierto === c.id ? null : c.id)} className="text-left">
                          <div className="font-medium text-gray-800 hover:text-azul">{c.nombre || '—'}</div>
                          <div className="text-xs text-gray-400">
                            {c.participaciones} {c.participaciones === 1 ? 'participación' : 'participaciones'} · {abierto === c.id ? 'ocultar ▲' : 'detalle ▼'}
                          </div>
                        </button>
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-gray-600">
                        {c.telefono && (
                          <div>
                            {c.telefono}
                            {whatsapp(c.telefono) && !c.baja && (
                              <a href={whatsapp(c.telefono)} target="_blank" rel="noreferrer" className="ml-2 text-green-600 font-bold hover:underline">WhatsApp</a>
                            )}
                          </div>
                        )}
                        {c.correo && <div className="break-all">{c.correo}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-gray-600">{c.marca_bateria || '—'}</td>
                      <td className="py-2.5 pr-3">
                        {c.baja ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">Dado de baja</span>
                        ) : c.acepta_ofertas ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">Sí</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-500">Sin autorización</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-gray-400 whitespace-nowrap">{fecha(c.ultima_interaccion)}</td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        {c.baja ? (
                          <button onClick={() => accion(c, 'reactivar')}
                            className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-green-50 hover:border-green-300 hover:text-green-700">
                            Reactivar
                          </button>
                        ) : (
                          <button onClick={() => accion(c, 'baja')}
                            className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-red-50 hover:border-red-300 hover:text-red-600">
                            Dar de baja
                          </button>
                        )}
                      </td>
                    </tr>
                    {abierto === c.id && (
                      <tr>
                        <td colSpan={6} className="pb-4">
                          <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600 space-y-1.5">
                            <p><b>Primer contacto:</b> {c.origen || '—'} · {fecha(c.creado_en)}</p>
                            {c.consentimiento_en ? (
                              <>
                                <p><b>Autorizó ofertas:</b> {fecha(c.consentimiento_en)} · {c.consentimiento_origen}</p>
                                <p className="italic">“{c.consentimiento_texto}”</p>
                              </>
                            ) : (
                              <p><b>Autorización de ofertas:</b> no la ha dado.</p>
                            )}
                            {c.baja && <p className="text-red-600"><b>Baja:</b> {fecha(c.baja_en)} · {c.baja_motivo}</p>}
                            <div className="pt-2">
                              <button onClick={() => accion(c, 'eliminar')} className="text-red-600 font-bold hover:underline">
                                🗑️ Borrar sus datos de la base de clientes
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {datos && datos.total > POR_PAGINA && (
          <div className="flex justify-between items-center mt-4 text-sm">
            <span className="text-gray-400">{datos.total.toLocaleString('es-CR')} clientes · página {pagina} de {paginas}</span>
            <div className="flex gap-2">
              <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1}
                className="border border-gray-200 rounded-lg px-3 py-1.5 disabled:opacity-40">← Anterior</button>
              <button onClick={() => setPagina(p => Math.min(paginas, p + 1))} disabled={pagina >= paginas}
                className="border border-gray-200 rounded-lg px-3 py-1.5 disabled:opacity-40">Siguiente →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
